'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ClaireFailureGroupView } from '@borradh-workspace/api-client/types';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { FIX_AGENT_RUNS_URL, fixAgentCommand } from './failure-format';

/**
 * How to hand a group to the Claire fix agent. It runs in GitHub Actions, so
 * for now the page gives the command to start it rather than starting it.
 */
export function StartFixDialog({
  group,
  open,
  onOpenChange,
}: {
  group: ClaireFailureGroupView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const command = fixAgentCommand(group);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast('Command copied', {
        description: 'Run it in a terminal signed in with the GitHub CLI.',
      });
    } catch {
      toast.error("Couldn't copy the command — select it and copy instead.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Start a fix for “{group.title}”</DialogTitle>
          <DialogDescription>
            The fix agent reads this group’s confirmed replies, changes Claire’s
            prompt or tool code, and replays the replies until the change fixes
            them (at most three tries). A passing fix arrives as a draft PR
            assigned to Ethan, who owns fix PRs; otherwise the run says why it
            stopped. Only replies that already have a regression case are used.
          </DialogDescription>
        </DialogHeader>
        <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs whitespace-pre-wrap break-all">
          {command}
        </pre>
        <p className="text-muted-foreground text-sm">
          {group.members.length} confirmed{' '}
          {group.members.length === 1 ? 'reply' : 'replies'}. When the PR opens,
          paste its link on the group and set it to Fix in progress.
        </p>
        <DialogFooter>
          <Button variant="outline" asChild>
            <a href={FIX_AGENT_RUNS_URL} target="_blank" rel="noreferrer">
              Runs <ExternalLink />
            </a>
          </Button>
          <Button onClick={copy}>
            <Copy />
            Copy command
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
